import React, { useState, useContext, useEffect } from 'react';
import { Box, Button, FormControl, FormControlLabel, Grid, InputLabel, MenuItem } from '@mui/material';
import { Field, FormikProps, FormikProvider } from 'formik';
import { Switch, TextField } from 'formik-mui';
import { useTranslation } from 'react-i18next';
import Select from '@mui/material/Select';

import { ToastSeverityEnum, WrapFormView } from '../';
import { AccountSettings, MediaImage, MustBeLogged } from '../';
import { requestAccountDeletion, requestResetPassword, updateMyself } from '../../api';
import { getItemRequest } from '../../api';
import Settings from '../../data/settings';
import { Media, User } from '../../models';
import { LayoutContext, ILayoutContext, DialogModesEnum } from '..';
import { useUderlyStore } from '../../zustand';

export interface AccountProps {
  PreFormView?: React.FC<{ formikProps: FormikProps<any> }>;
  onUpdated?: (user: User, hasChangedLanguage: boolean) => void;
}

const AccountSection = (props: { title: string }) => {
  return (
    <Grid item xs={12} container justifyContent="center">
      <Grid item xs={12} md={6}>
        <h2>{props.title}</h2>
      </Grid>
    </Grid>);
}

const Account = ({ PreFormView, onUpdated }: AccountProps) => {
  const layoutContext = useContext(LayoutContext) as ILayoutContext;
  const { t } = useTranslation();

  const [languages, setLanguages] = useState<[] | undefined | null>(null);

  const user = useUderlyStore((state) => state.user);
  const setUser = useUderlyStore((state) => state.setUser);

  useEffect(() => {
    const fetchUserData = async () => {
      const response2 = await getItemRequest(Settings.apiUrl + '/languages');

      if (response2 && response2.status === 200) {
        setLanguages(response2.data.data);
      }
    }

    fetchUserData();
  }, []);

  const onImageFileChanged = async (imageFile: File, { values, setFieldValue }: FormikProps<any>) => {
    const m = {
      name: imageFile.name,
      size: imageFile.size,
      mime_type: imageFile.type,
    };

    const updatedMedia = !values.media ? [m] : [...values.media, m];

    if (values)
      setFieldValue("media", updatedMedia);
  };

  const onImageRemoved = (media: Media, { values, setFieldValue }: FormikProps<any>) => {
    if (media && values.media)
      setFieldValue("media", values.media.filter((m: Media) => m.id !== media.id));
  }

  const view = (item: any, formik: FormikProps<any>) => {
    const { values, setFieldValue } = formik;

    return (
      <FormikProvider value={formik}>
        <Grid container spacing={2}>
          <Grid item xs={12} paddingBottom={2}>
            <MediaImage
              apiPath='/users'
              variant="rounded"
              imageUrl={(item.id && item.media && item.media.length > 0) ? (Settings.apiUrl + "/users/" + item.id + "/image") : null}
              item={item}
              onImageRemoved={(media: Media) => onImageRemoved(media, formik)}
              onImageFileChanged={(imageFile: File) => onImageFileChanged(imageFile, formik)} />
          </Grid>

          {PreFormView && <PreFormView formikProps={formik} />}

          <AccountSection title={t('something-about-you')} />

          <Grid item xs={12} md={6}>
            <Field component={TextField} name='name' type='text' label={t('name')} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Field component={TextField} name='surname' type='text' label={t('surname')} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Field component={TextField} name='mobile_phone' type='text' label={t('phone')} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Field component={TextField} name='email' type='text' label={'Email *'}
              InputProps={{ disabled: true }} />
          </Grid>

          <AccountSection title={t('settings')} />

          <Grid item xs={12} container justifyContent="center">
            <Grid item xs={12} md={5}>
              <FormControl>
                <InputLabel id="language-select-label">{t('language')}</InputLabel>
                {languages && (
                  <Select
                    labelId="language-select-label"
                    id="language-select"
                    value={!values.language_id ? "" : values.language_id}
                    label={t('language')}
                    onChange={(e: any) => setFieldValue('language_id', e.target.value as number)}
                  >
                    {languages.map((l: any) => <MenuItem key={"language-" + l.id} value={l.id}>{l.name}</MenuItem>)}
                  </Select>)}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={10} padding={2}>
              <FormControlLabel
                style={{ display: "flex", justifyContent: "center" }}
                control={<Field component={Switch} type="checkbox" name="allow_newsletter" />}
                label={t('allow-newsletter')} />
            </Grid>

            {formik.values.user_settings &&
              <Grid item xs={12} md={10} padding={2}>
                <AccountSettings formikProps={formik} onSettingsChanged={settings => setFieldValue("user_settings", settings)} />
              </Grid>}
          </Grid>

          <AccountSection title={t('options')} />

          <Grid item xs={12} container justifyContent="center" gap={1}>
            <Button variant="contained" className="danger-button"
              onClick={onRequestAccountDeletion}>{t('delete-account')}</Button>

            <Button variant="contained" className="warning-button"
              onClick={onSetOrChangePassword}>{t('set-change-password')}</Button>
          </Grid>
        </Grid>
      </FormikProvider>
    )
  }

  const onSubmit = async (item: any) => {
    const response = await updateMyself(item);
    console.log(item, response)

    if (response && response.data) {
      if (onUpdated) onUpdated(response.data, !user ? false : user.language_id !== response.data.language_id)

      setUser(response.data);
    }
  }

  const onDeleteAccount = async () => {
    const response = await requestAccountDeletion();
    console.log(response);
  }

  const onRequestAccountDeletion = () => {
    layoutContext.dialog.show({
      title: t('delete-account'),
      content: <>
        <p>{t('delete-account-desc')}</p>
        <p>{t('delete-account-desc2')}</p>
      </>,
      mode: DialogModesEnum.confirmation,
      onConfirm: onDeleteAccount
    });
  }

  const setOrChangePassword = async () => {
    layoutContext.dialog.showBackdrop();

    const response = await requestResetPassword(user?.email!);

    if (response && response.status === 200)
      layoutContext.toast.show({
        open: true,
        content: t('set-change-password-confirmation'),
        severity: ToastSeverityEnum.warning
      });

    layoutContext.dialog.hideBackdrop();
    layoutContext.dialog.hide();
  }

  const onSetOrChangePassword = () => {
    layoutContext.dialog.show({
      title: t('set-change-password'),
      className: "set-change-password-dialog",
      content: <>
        <p>{t('set-change-password-desc')}</p>
        <Button variant="contained" className="warning-button"
          onClick={setOrChangePassword}>{t('reset-password')}</Button>
      </>,
      mode: DialogModesEnum.standard
    });
  }

  return <Box className="account-page" style={!user ? { display: "flex", justifyContent: "center" } : {}}>
    {!user && <MustBeLogged />}

    {user && (
      <WrapFormView
        hideHeader
        title={t('your-account')}
        view={view}
        item={user as any}
        onSubmit={onSubmit}
        disableCreate />)}
  </Box>;
};

export default Account;
